import itiriri from 'itiriri';
import React, {useEffect} from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';
import {useAppState} from '../../context/AppStateProvider';
import {Button} from '../../kit/Button';
import {Grid} from '../../kit/Grid';
import {useAsync} from '../../kit/hooks/UseAsync';
import {Paper} from '../../kit/Paper';
import {Intent} from '../../kit/Theme/Theme';
import {SyncCenterProvider, useSyncCenter} from '../../syncstream/SyncCenterProvider';
import {useStore} from '../storage/hooks/UseStore';
import {getErrorMessage, usermanager} from "./Client";
import {useResetDashboardContext} from "../../pages/Dashboard/DashboardContext";
import {toasts} from "../../kit/Toasts/Toaster";
import queryString from 'query-string';
import { FormGroup } from '../../kit/Forms/FormGroup';
import { useForm } from "react-hook-form";
import { Form } from "../../kit/Forms/Form";
import { TextInput } from "../../kit/Forms/TextInput";
import axios from "axios";
import {appconfig} from "../../appconfig";
import { markGlobalPermissionsRefreshRequiredAfterLogin } from "../../core/authn/LoginState";

const Title = styled.h1`
  color: ${(p) => p.theme.backgrounds.default.fg};
  font-size: 7em;
  line-height: 0.9;
  letter-spacing: -0.016em;
  margin-bottom: 0;
`;

const Tagline = styled.div`
  color: ${(p) => p.theme.backgrounds.default.fg};
  font-size: 1.35rem;
  font-weight: 200;
`;

const Splash = styled.div`
  background-image: url(${require('./splash.png')});
  background-size: cover;
  width: 100vw;
  height: 100vh;
  padding: 2em;
  p,
  h3,
  a {
    color: ${(p) => p.theme.backgrounds.default.fg};
  }
  a {
    text-decoration: underline;
  }
  a span {
    font-size: 1.25rem;
    font-weight: bold;
  }
`;

const LoginContainer = styled(Grid)`
    text-align: center;
    width: fit-content;
    label {
        color: ${(p) => p.theme.backgrounds.default.fg} !important;
        font-size: 1.35rem;
        font-weight: 200;
    }
    input:focus {
        outline: none;
    }
`;

const LoginButton = styled(Button)`
  min-width: 310px;
`;

const WarningPaper = styled(Paper)`
  max-width: fit-content;
`;

type LoginPageProps = {
    loading?: boolean;
    flash?: string;
}

export function LoginPage(props: LoginPageProps) {
  return (
    <SyncCenterProvider disableSyncing>
      <LoginPageContent loading={props.loading} flash={props.flash} />
    </SyncCenterProvider>
  );
}

type LoginPageContentProps = {
    loading?: boolean;
    flash?: string;
}

function LoginPageContent(props: LoginPageContentProps) {
  //console.count('LoginPageContent');
  const {flash} = props;
  useResetDashboardContext();
  const appState = useAppState();
  const services = useSyncCenter();
  const usersStore = useStore(services.users.store).store;
  const [logoutMessageDisplayed, setLogoutMessageDisplayed] = React.useState(false);

  const hasQueuedData = useAsync(async () => await services.syncCenter.hasQueuedData());

  const queuedDataUser = itiriri(usersStore.values()).find(
    (user) => user.subjectId === appState.state.lastSyncedUserSub,
  );

  useEffect(() => {
    appState.set({selectedFacilityGroupId: ""});
  });

  const queuedDataUserName = queuedDataUser
    ? `${queuedDataUser.firstName} ${queuedDataUser.familyName}`
    : 'Unknown User';

  // const onClick = async () => {
  //   appState.set({ isInitialSyncComplete: false, isLocked: false });
  //   try {
  //     await usermanager.signinPopup();
  //   }
  //   catch (err: any) {
  //     if (err.stack.includes('Popup window closed')) {
  //       toasts.warning('Login window closed without logging in.');
  //     } else {
  //       Sentry.captureException(err);
  //       window.alert(getErrorMessage(err));
  //     }
  //   }
  // };

  const parsed = queryString.parse(location.search);

  useEffect(() => {
    if (flash === 'IndexedDBError' && !logoutMessageDisplayed) {
      toasts.toast(
          `Browser cache was cleared. Need to log back in.`,
          {
            timeoutSeconds: 10,
            intent: Intent.Danger
          }
      );
      setLogoutMessageDisplayed(true);
    }
    else if (flash === 'UserInactive' && !logoutMessageDisplayed) {
      toasts.toast(
          'You have been inactive for 10 minutes and have no pin set, so you have been logged out.',
          {
            timeoutSeconds: 30,
            intent: Intent.Danger
          }
      );
      setLogoutMessageDisplayed(true);
    }
    else if (flash === 'NotRegistered' && !logoutMessageDisplayed) {
        toasts.toast(
            'You are not registered to use MediSphere. Please contact your facility administrator.',
            {
                timeoutSeconds: 30,
                intent: Intent.Danger
            }
        );
        setLogoutMessageDisplayed(true);
    }
    else if (flash) {
        toasts.toast(flash, {
            timeoutSeconds: 10,
            intent: Intent.Danger
        });
    }
  }, []);

  return (
      <Splash>
          <Grid colsMobile={1} cols={2}>
              <Grid cols={1} gap={2}>
                  <img src={require('./mps-medica-logo.svg')} width="140" height="96" alt="MPS Medica Logo" />
                  <Title>MediSphere</Title>
                  <Tagline>Making medication administration safe for everyone</Tagline>
                  <LoginForm disabled={hasQueuedData.loading || Boolean(props.loading)} />
              </Grid>
              <div />
          </Grid>
      </Splash>
  );
}

type FormValues = {
    email: string;
};

type LoginFormProps = {
    disabled: boolean;
};

function LoginForm(props: LoginFormProps) {
    const {disabled} = props;
    const form = useForm<FormValues>();
    const appState = useAppState();

    const handleSubmit = async (data: FormValues) => {
        appState.set({ isInitialSyncComplete: false, isLocked: false });
        const url = `${appconfig.API_URL}/session?email=${encodeURIComponent(data.email)}`;
        const response = await axios.get(url, {
            validateStatus: (status) => status < 500
        });

        // Note that we expect a 200 status from the server. It is not possible to
        // process a 302 (or any other redirect) because axios uses XHR under the hood,
        // and XHR handles all redirects itself and this is not configurable.
        const location = response.headers.location ?? response.data?.location;
        if (response.status === 200 && location) {
            markGlobalPermissionsRefreshRequiredAfterLogin();
            window.location.assign(location);
        }
        else {
            const message = response.data.detail ?? response.data.title ?? `HTTP status code ${response.status}`;
            form.setError('email', { message: message, shouldFocus: true });
        }
    }

    if (disabled) {
        return null;
    }

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <LoginContainer cols={1} gap={2}>
                <FormGroup label="Email Address" fullWidth isRequired>
                    {/* note that email address validation occurs on the server */}
                    <TextInput name="email" type="email" fullWidth autoComplete="off" required autoFocus />
                </FormGroup>
                <LoginButton type="submit" large disabled={disabled}>
                    Log in
                </LoginButton>
            </LoginContainer>
        </Form>
    );
}
